import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Plus } from 'lucide-react';
import { useSections } from '../hooks/useSections';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { api } from '../lib/api';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';

export default function Home() {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const { data, isLoading } = useSections();

  if (isLoading) return <div className="text-sm text-gray-500">Načítám…</div>;

  const sections = data?.sections ?? [];

  if (sections.length === 0 && user?.role !== 'admin') {
    return (
      <div className="mx-auto max-w-md py-20 text-center">
        <h1 className="mb-2 font-heading text-2xl font-bold text-dark">Žádné sekce</h1>
        <p className="text-sm text-gray-600">
          Zatím ti nebyly přidělené žádné sekce ani kapitoly. Kontaktuj admina, pokud to považuješ za chybu.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold text-dark">BuyDubai Docs</h1>
          <p className="mt-1 text-sm text-gray-600">
            {sections.length} {sections.length === 1 ? 'sekce' : 'sekcí'} — vyber oblast, se kterou chceš pracovat.
          </p>
        </div>
        {user?.role === 'admin' && <NewSectionButton />}
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
        {sections.map((s) => (
          <Link
            key={s.id}
            to={`/s/${s.slug}`}
            className="card flex flex-col p-6 transition-all hover:-translate-y-0.5 hover:border-teal hover:shadow-lift"
          >
            <div className="mb-4 grid h-11 w-11 place-items-center rounded-md bg-teal-light font-heading font-bold text-teal">
              <FileText className="h-5 w-5" />
            </div>
            <h3 className="mb-2 font-heading text-xl font-bold text-dark">
              {(lang === 'en' && s.titleEn) || s.titleCs}
            </h3>
            <p className="mb-5 flex-1 text-sm leading-relaxed text-gray-600">
              {(lang === 'en' && s.descriptionEn) || s.descriptionCs || (lang === 'en' ? 'No description.' : 'Bez popisu.')}
            </p>
            <div className="flex items-center justify-end text-teal">
              <ArrowRight className="h-4 w-4" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function NewSectionButton() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  const m = useMutation({
    mutationFn: () => api.post('/sections', { title_cs: title, description_cs: description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sections'] });
      setOpen(false);
      setTitle('');
      setDescription('');
    },
  });

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-primary">
        <Plus className="h-4 w-4" /> Nová sekce
      </button>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
      className="card flex gap-3 p-4"
    >
      <input
        className="input"
        placeholder="Název sekce"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        autoFocus
      />
      <input
        className="input"
        placeholder="Popis"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <button type="submit" className="btn-primary" disabled={!title || m.isPending}>
        Vytvořit
      </button>
      <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
        Zrušit
      </button>
    </form>
  );
}
